/**
 * EventImage component for displaying an event's cover image
 * Falls back to the default illustration when no image is set, or when
 * the image URL fails to load (e.g. broken/404 file on the Drupal backend).
 * Shared component used in both EventCard and EventDetail.
 */

'use client';

import { Calendar } from 'lucide-react';
import { useState, type ComponentType } from 'react';

export interface EventDefaultIllustrationProps {
  eventTypeColor?: string | null;
  className?: string;
}

/**
 * Default illustration shown when an event has no usable image
 */
export function EventDefaultIllustration({
  eventTypeColor,
  className = '',
}: EventDefaultIllustrationProps) {
  return (
    <div
      className={`flex h-full w-full items-center justify-center bg-gradient-to-br from-primary/20 to-primary/5 ${className}`}
      style={{
        backgroundColor: eventTypeColor ? `${eventTypeColor}15` : undefined,
      }}
    >
      <Calendar
        className="h-12 w-12 opacity-30 sm:h-16 sm:w-16"
        style={{ color: eventTypeColor || undefined }}
      />
    </div>
  );
}

export interface EventImageProps {
  imageUrl: string | null | undefined;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  sizes?: string;
  loading?: 'lazy' | 'eager';
  eventTypeColor?: string | null;
  /** Image component supplied via EventsComponentsProvider (e.g. next/image) */
  ImageComponent: ComponentType<any>;
}

/**
 * Renders the event image, falling back to the default illustration when
 * there is no image URL, or when the image fails to load at runtime.
 */
export function EventImage({
  imageUrl,
  alt,
  width = 800,
  height = 450,
  className = 'h-full w-full object-cover transition-transform duration-300',
  sizes,
  loading,
  eventTypeColor,
  ImageComponent,
}: EventImageProps) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);

  if (!imageUrl || imageUrl === failedUrl) {
    return <EventDefaultIllustration eventTypeColor={eventTypeColor} />;
  }

  return (
    <ImageComponent
      key={imageUrl}
      src={imageUrl}
      alt={alt}
      width={width}
      height={height}
      className={className}
      sizes={sizes}
      loading={loading}
      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      onError={() => setFailedUrl(imageUrl)}
    />
  );
}
